import React, { lazy, Suspense } from 'react';

interface Opts {
  fallback: React.ReactNode;
}
type Unpromisify<T> = T extends Promise<infer P> ? P : never;

// Enhanced error boundary for chunk loading failures
class ChunkErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode; fallback?: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Chunk loading error:', error, errorInfo);

    // Try to reload the page on chunk loading errors
    if (error.message.includes('Loading chunk') || error.message.includes('ChunkLoadError')) {
      console.log('Attempting to reload page due to chunk loading error...');
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div style={{
          padding: '20px',
          textAlign: 'center',
          color: '#fff',
          fontFamily: 'Jura, sans-serif'
        }}>
          <h3>Loading Error</h3>
          <p>There was an issue loading this page. Reloading...</p>
        </div>
      );
    }

    return this.props.children;
  }
}

export const lazyLoad = <
  T extends Promise<any>,
  U extends React.ComponentType<any>,
>(
  importFunc: () => T,
  selectorFunc?: (s: Unpromisify<T>) => U,
  opts: Opts = { fallback: null },
) => {
  let lazyFactory: () => Promise<{ default: U }> = importFunc;

  if (selectorFunc) {
    lazyFactory = () =>
      importFunc().then(module => ({ default: selectorFunc(module) }));
  }

  const LazyComponent = lazy(lazyFactory);

  return (props: React.ComponentProps<U>): JSX.Element => (
    <ChunkErrorBoundary fallback={opts.fallback}>
      <Suspense fallback={opts.fallback!}>
        <LazyComponent {...props} />
      </Suspense>
    </ChunkErrorBoundary>
  );
};
